import { HttpClient, HttpEvent, HttpEventType, HttpParams, HttpResponse } from "@angular/common/http";
import { EventEmitter, inject, Injectable } from "@angular/core";
import { firstValueFrom, last, Observable, Subscriber, switchMap, tap } from "rxjs";
import { Paged } from "src/app/common/models/paged.model";
import { AbstractService } from "src/app/common/services/abstract.service";
import { ConfigService } from "src/app/common/services/config.service";
import { SegmentSpeakerService } from "src/app/common/services/segment-speaker.service";
import { Speech2TextServiceType } from "src/app/common/services/speech2text-language.service";
import { Audio, deserializeToAudio } from "src/app/modules/audio/models/audio.model";
import { SpeechSegment } from "src/app/modules/speech2text/models/speech-segment.model";

const API_PATH = "/api/internal/audio";

@Injectable({
  providedIn: "root",
})
export class AudioService extends AbstractService implements SegmentSpeakerService<Audio> {
  private readonly httpClient = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly audioUpdated = new EventEmitter<Audio>();

  upload(
    file: File,
    title: string,
    serviceType: Speech2TextServiceType,
    language: string,
    locale: string,
    entities: string[],
    generateThumbnail = false,
  ): Observable<HttpEvent<string>> {
    return new Observable<HttpEvent<string>>((subscriber) => {
      void this.handleUploadS3(file, title, serviceType, language, locale, entities, subscriber, generateThumbnail);
    });
  }

  private async handleUploadS3(
    file: File,
    title: string,
    serviceType: Speech2TextServiceType,
    language: string,
    locale: string,
    entities: string[],
    subscriber: Subscriber<HttpEvent<string>>,
    generateThumbnail = false,
  ): Promise<void> {
    const extension = file.name.split(".").pop();
    const id = crypto.randomUUID();
    const filepath = `${id}.${extension}`;
    const uploadUrl = await this.getUploadUrl(filepath);
    // Perform the request and handle the response
    this.httpClient
      .request("PUT", uploadUrl, { body: file, reportProgress: true, responseType: "blob", observe: "events" })
      .pipe(
        tap((event: HttpEvent<Blob>) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            // Emit the progress to the subscriber
            subscriber.next({ type: HttpEventType.UploadProgress, loaded: event.loaded, total: event.total });
          }
        }),
        last(), // Only pass through the last event (which should be the response)
        switchMap(() => {
          return this.httpClient.post(`${API_PATH}/upload`, {
            title,
            serviceType,
            language,
            locale,
            entities,
            filepath,
            generateThumbnail,
          });
        }),
      )
      .subscribe({
        next: () => {
          subscriber.next(new HttpResponse<string>({ body: id }));
          subscriber.complete();
        },
        error: (err: unknown) => {
          subscriber.error(err);
        },
      });
  }

  async getUploadUrl(filename: string): Promise<string> {
    return await firstValueFrom(this.httpClient.post<string>(`${API_PATH}/presigned-upload-url`, filename));
  }

  async setSummary(audioId: string, summary: string): Promise<Audio> {
    const url = `${API_PATH}/${audioId}/summary`;
    return deserializeToAudio(await firstValueFrom(this.httpClient.put<Audio>(url, summary)));
  }

  async fetchPagedAudios(params: HttpParams): Promise<Paged<Audio> | null> {
    const source = this.httpClient.get<Paged<Audio>>(API_PATH, { params });
    const audios = await this.cancelPrevious("fetchPagedAudios", source);
    if (audios) {
      audios.elements = audios.elements?.map((audio) => deserializeToAudio(audio));
    }
    return audios;
  }

  async fetchAudio(id: string): Promise<Audio> {
    const url = `${API_PATH}/${id}`;
    return deserializeToAudio(await firstValueFrom(this.httpClient.get<Audio>(url)));
  }

  async delete(id: string): Promise<void> {
    await firstValueFrom(this.httpClient.delete<undefined>(`${API_PATH}/${id}/delete`, {}));
  }

  async setSegmentSpeaker(id: string, speakerId: string, speakerEntityId?: string): Promise<Audio> {
    const url = `${API_PATH}/${id}/speaker/${speakerId}`;
    const result = deserializeToAudio(await firstValueFrom(this.httpClient.put<Audio>(url, speakerEntityId)));
    this.onUpdate(result);
    return result;
  }

  async removeSegmentSpeaker(id: string, speakerId: string): Promise<Audio> {
    const url = `${API_PATH}/${id}/speaker/${speakerId}`;
    const result = deserializeToAudio(await firstValueFrom(this.httpClient.delete<Audio>(url)));
    this.onUpdate(result);
    return result;
  }

  onUpdate(update: Audio): void {
    this.audioUpdated.emit(update);
  }

  getUpdates(): Observable<Audio> {
    return this.audioUpdated;
  }

  async saveSegments(id: string, start: number, texts: string[], language?: string): Promise<SpeechSegment[]> {
    const url = `${API_PATH}/${id}/segments/${language || "-"}/${start}`;
    return await firstValueFrom(this.httpClient.put<SpeechSegment[]>(url, texts));
  }

  async translate(id: string, language: string, force: boolean): Promise<SpeechSegment[]> {
    const url = `${API_PATH}/${id}/translate`;
    const audio: Audio = await firstValueFrom(this.httpClient.post<Audio>(url, { language, force }));
    return audio.segments;
  }

  async dubbing(id: string, language: string, force: boolean): Promise<Audio> {
    const url = `${API_PATH}/${id}/dub/${language}`;
    const audio: Audio = await firstValueFrom(this.httpClient.post<Audio>(url, { language, force }));
    return audio;
  }

  getAwsCloudFrontDistributionUrl(): string {
    return this.configService.cloudfrontUrl;
  }
}
